import { useCallback, useRef, useMemo, useEffect } from 'react';
import { useConnectionState, useToastState } from '../store/useAppStore';

// Utility function for exponential backoff retries
const exponentialBackoff = async <T>(
  fn: () => Promise<T>,
  maxRetries: number = 3,
  baseDelay: number = 1000,
  maxDelay: number = 10000
): Promise<T> => {
  let lastError: Error;
  
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error as Error;
      
      // Don't retry on last attempt
      if (attempt === maxRetries) {
        break;
      }
      
      // Skip retry for non-retryable errors
      if (error instanceof Response) {
        // Don't retry 400 Bad Request, 401 Unauthorized, 403 Forbidden, 404 Not Found
        if ([400, 401, 403, 404].includes(error.status)) {
          throw error;
        }
      }
      
      // Calculate delay with exponential backoff and jitter
      const delay = Math.min(
        baseDelay * Math.pow(2, attempt) + Math.random() * 1000,
        maxDelay
      );
      
      console.log(`🔄 Retry attempt ${attempt + 1}/${maxRetries} after ${delay}ms delay`);
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
  
  throw lastError!;
};

export const useConnection = () => {
  const {
    isConnected,
    isLoading,
    authId,
    setConnectionStatus,
    setLoading,
    setQRCode
  } = useConnectionState();
  
  const { showToast } = useToastState();

  // Create a stable ref to avoid circular dependencies
  const startConnectionRef = useRef<() => Promise<void>>();
  const lastConnectionAttempt = useRef<number>(0);
  const connectionAttempts = useRef<number>(0);
  const healthCheckInterval = useRef<number | null>(null);
  const autoRecoveryAttempts = useRef<number>(0);
  const maxAutoRecoveryAttempts = 5;

  const startConnection = useCallback(async () => {
    if (!authId) {
      console.log('🔌 Cannot start connection without authId');
      return;
    }

    if (isLoading) {
      console.log('🔌 Connection already in progress');
      return;
    }

    // Check if already connected to prevent unnecessary attempts
    if (isConnected) {
      console.log('🔌 Already connected, skipping connection attempt');
      return;
    }

    // Prevent rapid-fire connection attempts (stronger debounce - increased to 15s)
    const now = Date.now();
    if (now - lastConnectionAttempt.current < 15000) {
      console.log('🔌 Connection attempt too soon, debouncing (15s)');
      return;
    }

    // Limit connection attempts to prevent infinite loops (reduced from 3 to 2)
    if (connectionAttempts.current >= 2) {
      console.log('🔌 Maximum connection attempts reached (2)');

      // Reset attempts after 5 minutes
      setTimeout(() => {
        connectionAttempts.current = 0;
        console.log('🔄 Connection attempts counter reset');
      }, 300000);
      
      showToast({
        type: 'error',
        title: 'Muitas Tentativas',
        message: 'Limite de 2 tentativas atingido. Aguarde 5 minutos ou recarregue a página.',
        duration: 10000
      });
      return;
    }

    lastConnectionAttempt.current = now;
    connectionAttempts.current++;

    console.log('🔌 Starting WhatsApp connection...', { authId });
    setLoading(true);
    
    try {
      await exponentialBackoff(async () => {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 30000);
        
        const response = await fetch(`${(import.meta as any).env.VITE_BACKEND_URL}/api/whatsapp/connect`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ authId }),
          signal: controller.signal
        });
        
        clearTimeout(timeoutId);

        if (!response.ok) {
          // Check if it's a rate limiting error
          if (response.status === 429) {
            const retryAfter = response.headers.get('Retry-After');
            const waitTime = retryAfter ? parseInt(retryAfter) * 1000 : 60000;
            
            console.log(`🔌 Rate limited, waiting ${waitTime}ms before retry`);
            await new Promise(resolve => setTimeout(resolve, waitTime));
            throw new Error(`Rate limited (429), waiting ${waitTime}ms`);
          }
          
          throw new Error(`Connection failed with status ${response.status}`);
        }

        const data = await response.json();
        console.log('🔌 Connection request successful', data);
        connectionAttempts.current = 0; // Reset counter on success
        autoRecoveryAttempts.current = 0; // Reset auto-recovery counter
        
        if (data.success) {
          // Check if already connected (backend responds "já conectado")
          const isAlreadyConnected = data.message && data.message.toLowerCase().includes('já conectado');
          
          if (isAlreadyConnected) {
            console.log('✅ [useConnection] Already connected - setting connected state');
            setConnectionStatus(true);
            setLoading(false);
            
            showToast({
              type: 'success',
              title: 'Já Conectado',
              message: 'WhatsApp já estava conectado!'
            });
          } else {
            showToast({
              type: 'info',
              title: 'Conectando',
              message: 'Iniciando conexão com WhatsApp...'
            });
          }
          
          // Start health monitoring after successful connection request
          startHealthCheck();
        }
        
        return data;
      }, 3, 2000, 15000); // 3 retries, 2s base delay, 15s max delay
      
    } catch (error) {
      console.error('🔌 Connection failed after retries:', error);
      setLoading(false);
      
      const isRateLimit = error instanceof Error && error.message.includes('429');
      
      showToast({
        type: 'error',
        title: isRateLimit ? 'Muitas Tentativas' : 'Falha na Conexão',
        message: isRateLimit 
          ? 'Aguarde alguns minutos antes de tentar conectar novamente.'
          : 'Não foi possível iniciar a conexão. Verifique sua internet.',
        duration: isRateLimit ? 10000 : 5000,
        action: !isRateLimit ? {
          label: 'Tentar Novamente',
          onClick: () => {
            // Use the ref to avoid circular dependencies
            if (startConnectionRef.current) {
              startConnectionRef.current();
            }
          }
        } : undefined
      });
    }
  }, [authId, isLoading, setLoading, showToast]);

  // Health check function (desabilitado durante conexão inicial)
  const performHealthCheck = useCallback(async () => {
    if (!authId || !isConnected || isLoading) {
      // Não executar health check durante loading/conexão inicial
      return;
    }

    try {
      const response = await fetch(`${(import.meta as any).env.VITE_BACKEND_URL}/api/whatsapp/${authId}/status`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      });

      if (!response.ok && response.status !== 404) {
        console.warn('🏥 Health check failed:', response.status);
        
        // If health check fails consistently, attempt auto-recovery
        if (autoRecoveryAttempts.current < maxAutoRecoveryAttempts) {
          autoRecoveryAttempts.current++;
          console.log(`🔄 Attempting auto-recovery (${autoRecoveryAttempts.current}/${maxAutoRecoveryAttempts})`);
          
          showToast({
            type: 'warning',
            title: 'Reconectando',
            message: 'Detectado problema de conexão. Tentando reconectar...',
            duration: 3000
          });
          
          // Attempt to restart connection
          setTimeout(() => {
            if (startConnectionRef.current) {
              startConnectionRef.current();
            }
          }, 2000);
        } else {
          console.error('🚨 Max auto-recovery attempts reached');
          stopHealthCheck();
          
          showToast({
            type: 'error',
            title: 'Conexão Perdida',
            message: 'Problemas persistentes de conexão. Reconecte manualmente.',
            duration: 10000,
            action: {
              label: 'Reconectar',
              onClick: () => {
                autoRecoveryAttempts.current = 0;
                if (startConnectionRef.current) {
                  startConnectionRef.current();
                }
              }
            }
          });
        }
      } else {
        // Health check successful, reset recovery attempts
        autoRecoveryAttempts.current = 0;
      }
    } catch (error) {
      console.warn('🏥 Health check error:', error);
    }
  }, [authId, isConnected, isLoading, showToast]);

  const startHealthCheck = useCallback(() => {
    // Clear existing interval
    if (healthCheckInterval.current) {
      window.clearInterval(healthCheckInterval.current);
    }
    
    // Start new health check interval (every 60 seconds)
    healthCheckInterval.current = window.setInterval(performHealthCheck, 60000);
    console.log('🏥 Health monitoring started');
  }, [performHealthCheck]);

  const stopHealthCheck = useCallback(() => {
    if (healthCheckInterval.current) {
      window.clearInterval(healthCheckInterval.current);
      healthCheckInterval.current = null;
      console.log('🏥 Health monitoring stopped');
    }
  }, []);

  // Update the ref whenever startConnection changes
  startConnectionRef.current = startConnection;

  const disconnect = useCallback(async () => {
    if (!authId) return;

    // Stop health monitoring
    stopHealthCheck();

    try {
      await fetch(`${(import.meta as any).env.VITE_BACKEND_URL}/api/whatsapp/logout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ authId })
      });

      setConnectionStatus(false);
      setQRCode(null);

      showToast({
        type: 'info',
        title: 'Desconectado',
        message: 'WhatsApp desconectado com sucesso.'
      });
    } catch (error) {
      console.error('🔌 Disconnect failed:', error);
      showToast({
        type: 'error',
        title: 'Erro ao Desconectar',
        message: 'Falha ao desconectar do WhatsApp.'
      });
    }
  }, [authId, setConnectionStatus, setQRCode, showToast, stopHealthCheck]);

  // Fallback to fetch QR code via HTTP if WebSocket doesn't receive it
  const fetchQRCodeFallback = useCallback(async () => {
    if (!authId) {
      console.log('🔄 Cannot fetch QR code without authId');
      return;
    }

    try {
      console.log('🔄 Fetching QR code via HTTP fallback...', { authId: authId.substring(0, 12) + '...' });
      
      const qrCode = await exponentialBackoff(async () => {
        const response = await fetch(`${(import.meta as any).env.VITE_BACKEND_URL}/api/whatsapp/${authId}/qr`);
        
        if (response.status === 404) {
          console.log('🔄 No QR code available (404)');
          return null;
        }
        
        if (response.status === 429) {
          const retryAfter = response.headers.get('Retry-After');
          const waitTime = retryAfter ? parseInt(retryAfter) * 1000 : 30000;
          console.log(`🔄 QR fetch rate limited, waiting ${waitTime}ms`);
          throw new Error(`Rate limited (429), waiting ${waitTime}ms`);
        }
        
        if (!response.ok) {
          throw new Error(`QR fetch failed with status ${response.status}`);
        }

        const data = await response.json();
        if (data.success && data.data?.qrCode) {
          console.log('🔄 QR code fetched successfully via HTTP fallback');
          return data.data.qrCode;
        }
        
        return null;
      }, 2, 1000, 5000); // 2 retries, 1s base delay, 5s max delay
      
      if (qrCode) {
        setQRCode(qrCode);
        return qrCode;
      }
      
    } catch (error) {
      console.error('🔄 QR code fallback fetch error after retries:', error);
    }
    
    return null;
  }, [authId, setQRCode]);

  // Start/stop health monitoring based on connection status
  useEffect(() => {
    if (isConnected) {
      startHealthCheck();
    } else {
      stopHealthCheck();
    }
    
    // Cleanup on unmount
    return () => {
      stopHealthCheck();
    };
  }, [isConnected, startHealthCheck, stopHealthCheck]);

  // Memoize connectionInfo to prevent infinite loops
  const connectionInfo = useMemo(() => ({
    status: isConnected ? 'connected' : (isLoading ? 'connecting' : 'disconnected'),
    isHealthy: isConnected && autoRecoveryAttempts.current < maxAutoRecoveryAttempts,
    canReconnect: !isLoading,
    recoveryAttempts: autoRecoveryAttempts.current,
    maxRecoveryAttempts: maxAutoRecoveryAttempts
  }), [isConnected, isLoading]);

  return {
    connectionInfo,
    startConnection,
    disconnect,
    fetchQRCodeFallback,
    startHealthCheck,
    stopHealthCheck
  };
};

export default useConnection;